import { useMemo } from 'react';
import { getTestId } from '../hooks/useTestId';
import { t } from '@lingui/core/macro';
import { Input } from 'src/components/ui';
import { useTheme } from '@emotion/react';
import { useDarkMode } from '../context/DarkModeContext';
import Icon from 'src/components/Icon';

function computePaging({ offset, size, count }) {
  const pageSize = Math.max(size, 1);
  const totalPages = Math.ceil(count / pageSize);
  if (totalPages <= 0)
    return { totalPages: 0, currentPage: 0, isFirst: true, isLast: true, prevOffset: 0, nextOffset: 0, firstOffset: 0, lastOffset: 0 };

  const lastPageIdx = totalPages - 1;
  const currentPage = Math.min(Math.floor(offset / pageSize), lastPageIdx);

  return {
    totalPages,
    currentPage,
    isFirst: currentPage === 0,
    isLast: currentPage === lastPageIdx,
    prevOffset: Math.max(0, currentPage - 1) * pageSize,
    nextOffset: Math.min(lastPageIdx, currentPage + 1) * pageSize,
    firstOffset: 0,
    lastOffset: lastPageIdx * pageSize,
  };
}

function buildPageItems(currentPage, totalPages) {
  if (totalPages <= 0) return [];
  const lastIdx = totalPages - 1;
  const wStart = Math.max(0, currentPage - 2);
  const wEnd = Math.min(lastIdx, currentPage + 2);
  const items = [];

  if (wStart > 0) {
    items.push({ type: 'page', page: 0 });
    if (wStart > 2) items.push({ type: 'ellipsis', key: 'el-l' });
    else if (wStart === 2) items.push({ type: 'page', page: 1 });
  }

  for (let p = wStart; p <= wEnd; p++) items.push({ type: 'page', page: p });

  if (wEnd < lastIdx) {
    if (wEnd < lastIdx - 2) items.push({ type: 'ellipsis', key: 'el-r' });
    else if (wEnd === lastIdx - 2) items.push({ type: 'page', page: lastIdx - 1 });
    items.push({ type: 'page', page: lastIdx });
  }

  return items;
}

const SZ = 28;

function Pagination({ offset, setOffset, size, setSize, sizes = [10, 20, 30], count }) {
  const theme = useTheme();
  const { isDarkMode } = useDarkMode();

  const sizeOptions = useMemo(() => {
    return sizes.map(size => ({ value: size, label: t`${size} / page` }));
  }, [sizes]);

  const { totalPages, currentPage, isFirst, isLast, prevOffset, nextOffset, firstOffset, lastOffset } = useMemo(
    () => computePaging({ offset, size, count }),
    [offset, size, count]
  );

  const pageItems = useMemo(() => buildPageItems(currentPage, totalPages), [currentPage, totalPages]);

  const textColor = isDarkMode ? '#878b9a' : '#555';
  const disabledColor = isDarkMode ? '#40444f' : '#ccc';
  const hoverBg = isDarkMode ? '#32363d' : '#f0f0f2';
  const ellipsisColor = isDarkMode ? '#40444f' : '#aaa';
  const primary = theme.primaryMain;

  return (
    <>
      <style>{`
        .pg-btn {
          background: none;
          border: none;
          border-radius: 6px;
          width: ${SZ}px;
          height: ${SZ}px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          cursor: pointer;
          transition: background 0.12s;
          flex-shrink: 0;
          line-height: 1;
        }
        .pg-btn:hover:not(:disabled) { background: ${hoverBg}; }
        .pg-btn:disabled { cursor: default; }
        .pg-btn--active {
          background: ${primary} !important;
          color: #fff !important;
          font-weight: 600;
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end', width: '100%', paddingBottom: 12 }}>
        {/* First */}
        <button
          type="button"
          className="pg-btn"
          disabled={isFirst}
          onClick={() => setOffset(firstOffset)}
          style={{ color: isFirst ? disabledColor : textColor, opacity: isFirst ? 0.4 : 1 }}
          {...getTestId('pagination', '.', 'first')}
        >
          <Icon icon="CaretDoubleLeft" style={{ fontSize: '0.75rem' }} />
        </button>

        {/* Prev */}
        <button
          type="button"
          className="pg-btn"
          disabled={isFirst}
          onClick={() => setOffset(prevOffset)}
          style={{ color: isFirst ? disabledColor : textColor, opacity: isFirst ? 0.4 : 1 }}
          {...getTestId('pagination', '.', 'prev')}
        >
          <Icon icon="CaretLeft" style={{ fontSize: '0.7rem' }} />
        </button>

        {/* Page numbers */}
        {pageItems.map(item =>
          item.type === 'ellipsis' ? (
            <span
              key={item.key}
              style={{ width: SZ, textAlign: 'center', color: ellipsisColor, fontSize: '0.85rem', userSelect: 'none', flexShrink: 0 }}
            >
              …
            </span>
          ) : (
            <button
              key={item.page}
              type="button"
              className={`pg-btn${item.page === currentPage ? ' pg-btn--active' : ''}`}
              style={{
                color: item.page === currentPage ? '#fff' : textColor,
                fontSize: '0.875rem',
              }}
              onClick={() => setOffset(item.page * size)}
              {...getTestId('pagination', '.', 'page', '-', item.page + 1)}
            >
              {item.page + 1}
            </button>
          )
        )}

        {/* Next */}
        <button
          type="button"
          className="pg-btn"
          disabled={isLast}
          onClick={() => setOffset(nextOffset)}
          style={{ color: isLast ? disabledColor : textColor, opacity: isLast ? 0.4 : 1 }}
          {...getTestId('pagination', '.', 'next')}
        >
          <Icon icon="CaretRight" style={{ fontSize: '0.7rem' }} />
        </button>

        {/* Last */}
        <button
          type="button"
          className="pg-btn"
          disabled={isLast}
          onClick={() => setOffset(lastOffset)}
          style={{ color: isLast ? disabledColor : textColor, opacity: isLast ? 0.4 : 1 }}
          {...getTestId('pagination', '.', 'last')}
        >
          <Icon icon="CaretDoubleRight" style={{ fontSize: '0.75rem' }} />
        </button>

        <Input
          type="select"
          className="form-control ml-2"
          style={{ width: 'fit-content', height: SZ, padding: '0 0.25rem', fontSize: '0.8rem', paddingRight: '30px' }}
          value={size}
          onChange={({ target: { value } }) => setSize(parseInt(value, 10))}
          {...getTestId('pagination', '.', 'size-select')}
        >
          {sizeOptions.map(s => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Input>
      </div>
    </>
  );
}

export default Pagination;
